/**
 * Authentication API Client
 * Provides type-safe API calls for LDA authentication operations.
 * @packageDocumentation
 */

import type { AuthUser, AuthRetailer, LoginRequest, LoginResponse, ReauthRequest } from '@/stores/auth';

const API_BASE = '/api/auth';

/**
 * Authentication API error class.
 */
export class AuthApiError extends Error {
  /**
   * Creates an AuthApiError.
   *
   * @param { number } status HTTP status code.
   * @param { string } code Error code.
   * @param { string } message Error message.
   */
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'AuthApiError';
    Object.setPrototypeOf(this, AuthApiError.prototype);
  }
}

/**
 * Handles API response and throws on error.
 *
 * @param { Response } response Fetch response object.
 * @returns { Promise<T> } Parsed JSON response.
 */
const handleResponse = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({
      error: { code: 'UNKNOWN', message: response.statusText },
    }));

    // Backend sends nested format: { error: { code, message } }
    // Also handle flat format: { error: "CODE", message: "..." }
    const err = errorBody.error;
    const errorCode =
      typeof err === 'object' && err !== null && 'code' in err
        ? String(err.code)
        : typeof err === 'string'
          ? err
          : 'UNKNOWN';
    const errorMessage =
      typeof err === 'object' && err !== null && 'message' in err
        ? String(err.message)
        : typeof errorBody.message === 'string'
          ? errorBody.message
          : response.statusText;

    throw new AuthApiError(response.status, errorCode, errorMessage);
  }

  return response.json() as Promise<T>;
};

/**
 * Current user response from /me endpoint.
 */
export interface MeResponse {
  user: AuthUser;
  retailers: AuthRetailer[];
}

/**
 * Auth config response from /config endpoint.
 */
export interface AuthConfigResponse {
  sessionTtlHours: number;
  googleAuth: {
    enabled: boolean;
    clientId?: string;
  };
}

/**
 * LDA health check response.
 */
export interface LdaHealthResponse {
  lastError?: string;
  status: 'healthy' | 'unhealthy';
  circuitBreaker: 'closed' | 'open' | 'half-open';
}

/**
 * Authentication API client for LDA authentication.
 */
export const authApi = {
  /**
   * Logs out the current session.
   *
   * @returns { Promise<void> } Void on success.
   */
  logout: async (): Promise<void> => {
    await fetch(`${API_BASE}/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  },

  /**
   * Gets current user information.
   *
   * @returns { Promise<MeResponse> } Current user info and retailers.
   */
  me: async (): Promise<MeResponse> => {
    const response = await fetch(`${API_BASE}/me`, {
      credentials: 'include',
    });

    return handleResponse<MeResponse>(response);
  },

  /**
   * Checks LDA service health.
   *
   * @returns { Promise<LdaHealthResponse> } LDA health status.
   */
  ldaHealth: async (): Promise<LdaHealthResponse> => {
    const response = await fetch('/api/health/lda', {
      credentials: 'include',
    });

    return handleResponse<LdaHealthResponse>(response);
  },

  /**
   * Gets authentication configuration.
   *
   * @returns { Promise<AuthConfigResponse> } Auth configuration.
   */
  config: async (): Promise<AuthConfigResponse> => {
    const response = await fetch(`${API_BASE}/config`, {
      credentials: 'include',
    });

    return handleResponse<AuthConfigResponse>(response);
  },

  /**
   * Sends a password reset email via LDA.
   * Server returns success even for unknown users to prevent account enumeration.
   * May throw AuthApiError on network or server errors.
   *
   * @param { string } username Username or email to send reset link for.
   * @returns { Promise<void> } Void on success.
   */
  forgotPassword: async (username: string): Promise<void> => {
    const response = await fetch(`${API_BASE}/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ username }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    await handleResponse<{ success: boolean }>(response);
  },

  /**
   * Authenticates user with LDA username and password.
   *
   * @param { LoginRequest } request Login credentials.
   * @returns { Promise<LoginResponse> } Login response with user and retailers.
   */
  login: async (request: LoginRequest): Promise<LoginResponse> => {
    const response = await fetch(`${API_BASE}/login`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleResponse<LoginResponse>(response);
  },

  /**
   * Re-authenticates user with password only (for session expired).
   *
   * @param { ReauthRequest } request Password for re-authentication.
   * @returns { Promise<LoginResponse> } Login response with user and retailers.
   */
  reauth: async (request: ReauthRequest): Promise<LoginResponse> => {
    const response = await fetch(`${API_BASE}/reauth`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(request),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleResponse<LoginResponse>(response);
  },

  /**
   * Verifies a Google ID token and authenticates the user.
   *
   * @param { string } idToken Google ID token (JWT credential).
   * @returns { Promise<LoginResponse> } Login response with user and retailers.
   */
  googleVerify: async (idToken: string): Promise<LoginResponse> => {
    const response = await fetch(`${API_BASE}/google/verify`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ idToken }),
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return handleResponse<LoginResponse>(response);
  },
};
